import { on } from '@disy/lib/event-util';
import type { Teardown } from '@disy/lib/teardown';
import { tearDown } from '@disy/lib/teardown';

import { getStore } from 'cadenza/store/cadenza-store';
import { hasDetailView } from 'cadenza/workbook/workbook-core-selectors.state';

interface Point {
  x: number;
  y: number;
}

export function enableTwoFingerPan(scrollBody: HTMLElement) {
  const tableTouchInteractions = new TwoFingerPanInteraction(scrollBody);
  return () => tableTouchInteractions.teardown();
}

class TwoFingerPanInteraction {
  #teardowns: Teardown[] = [];

  readonly #scrollBody: HTMLElement;
  readonly #pointers = new Map<number, Point>();
  #previousMid: Point | null = null;

  constructor(scrollBody: HTMLElement) {
    this.#scrollBody = scrollBody;
    this.#scrollBody.style.overflow = 'hidden';

    this.#teardowns.push(
      on(this.#scrollBody, 'pointerdown', (event: PointerEvent) => this.#onPointerDown(event)),
      on(this.#scrollBody, 'pointermove', (event: PointerEvent) => this.#onPointerMove(event)),
      on(this.#scrollBody, ['pointerup', 'pointercancel', 'pointerout', 'pointerleave'], (event: PointerEvent) =>
        this.#onPointerUp(event),
      ),
      on(
        this.#scrollBody,
        'touchmove',
        (event) => {
          if (event.touches.length === 2) {
            // Calling preventDefault() on pointermove did not prevent the browser from scrolling when using two fingers on
            // the screen - preventing the default of touchmove does.
            event.preventDefault();
          }
        },
        { passive: false },
      ),
    );
  }

  #onPointerDown(event: PointerEvent) {
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    const currentlyInDetailView = hasDetailView(getStore().getState());
    if (currentlyInDetailView) {
      // When the table view is in detail view, touch pan should be possible with one finger.
      if (this.#pointers.size === 1) {
        this.#scrollBody.style.overflow = 'auto';
      } else {
        this.#scrollBody.style.overflow = 'hidden';
      }
    } else {
      // When the detail view is not active, touch pan should be possible with two fingers.
      if (this.#pointers.size === 2) {
        this.#scrollBody.style.overflow = 'auto';
      } else {
        this.#scrollBody.style.overflow = 'hidden';
      }
    }
    // capture pointer so moves keep arriving even if the finger slips off the element
    (event.target as HTMLElement)?.setPointerCapture?.(event.pointerId);
  }

  #onPointerMove(event: PointerEvent) {
    if (!this.#pointers.has(event.pointerId)) {
      return;
    }

    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size !== 2) {
      this.#previousMid = null;
      return;
    }

    // two-finger pan (midpoint translation)
    const [p1, p2] = Array.from(this.#pointers.values());
    const mid = midpoint(p1, p2);

    if (this.#previousMid == null) {
      this.#previousMid = mid;
      return;
    }

    const dx = -1 * (mid.x - this.#previousMid.x);
    const dy = -1 * (mid.y - this.#previousMid.y);

    if (dx !== 0 || dy !== 0) {
      this.#panBy(dx, dy);
    }

    this.#previousMid = mid;
    event.preventDefault();
  }

  #onPointerUp(event: PointerEvent) {
    this.#pointers.delete(event.pointerId);

    if (this.#pointers.size !== 2) {
      this.#previousMid = null;
    }
  }

  #panBy(dx: number, dy: number) {
    this.#scrollBody.scrollBy({ left: dx, top: dy, behavior: 'auto' });
  }

  teardown() {
    this.#teardowns = tearDown(this.#teardowns);
  }
}

function midpoint(a: Point, b: Point): Point {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}
